import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import {
  ReactionSelector,
  useChannelStateContext,
  useDialog,
  useMessageContext,
} from "stream-chat-react";
import {
  CHAT_REACTION_OPTIONS,
  buildReactionOptions,
  collectExtraReactionTypes,
  encodeEmojiReactionType,
} from "./chatReactions";

// Stream's selector with the five quick reactions, plus a "+" that opens the
// full emoji picker; picking any emoji reacts with that emoji as the type.
function FullReactionSelector(props) {
  const { handleReaction, message } = useMessageContext("FullReactionSelector");
  const dialog = useDialog({ id: `reaction-selector--${message?.id}` });
  const [pickerOpen, setPickerOpen] = useState(false);
  const rootRef = useRef(null);
  const moreButtonRef = useRef(null);
  const pickerRef = useRef(null);
  const [openUp, setOpenUp] = useState(false);

  // The picker stays inside Stream's dialog (it has a focus trap, so a
  // portal couldn't take focus); flip it above the selector when there's no
  // room below the "+" button.
  useLayoutEffect(() => {
    if (!pickerOpen || !moreButtonRef.current) return undefined;
    const place = () => {
      const rect = moreButtonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUp(spaceBelow < PICKER_HEIGHT + 16 && rect.top > PICKER_HEIGHT + 16);
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [pickerOpen]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onClick = (event) => {
      const insideSelector = rootRef.current?.contains(event.target);
      const insidePicker = pickerRef.current?.contains(event.target);
      if (!insideSelector && !insidePicker) setPickerOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);

  return (
    <div className="rw-reaction-selector" ref={rootRef}>
      <div className="rw-reaction-selector__row">
        <ReactionSelector {...props} reactionOptions={CHAT_REACTION_OPTIONS} />
        <button
          ref={moreButtonRef}
          type="button"
          className={`rw-reaction-selector__more${pickerOpen ? " is-open" : ""}`}
          aria-label="More reactions"
          title="More reactions"
          onClick={() => setPickerOpen((open) => !open)}
        >
          +
        </button>
      </div>
      {pickerOpen && (
          <div
            className={`rw-reaction-selector__picker${openUp ? " rw-reaction-selector__picker--up" : ""}`}
            ref={pickerRef}
            // Keep keystrokes/clicks in the picker from reaching Stream's
            // message/dialog handlers
            onKeyDown={(event) => event.stopPropagation()}
            onKeyUp={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <Picker
              data={data}
              theme="light"
              autoFocus
              previewPosition="none"
              skinTonePosition="none"
              navPosition="bottom"
              perLine={9}
              maxFrequentRows={1}
              dynamicWidth={false}
              onEmojiSelect={(emoji, event) => {
                handleReaction(encodeEmojiReactionType(emoji.native), event);
                setPickerOpen(false);
                dialog?.close?.();
              }}
            />
          </div>
      )}
    </div>
  );
}

const PICKER_HEIGHT = 360;

export default FullReactionSelector;

// Keeps the channel's reaction options in step with the emoji people have
// actually used, so arbitrary-emoji reactions render in lists and modals.
export const useChannelReactionOptions = (channel) => {
  const [extraTypes, setExtraTypes] = useState(() =>
    collectExtraReactionTypes(channel)
  );

  const refresh = useCallback(() => {
    setExtraTypes((previous) => {
      const next = collectExtraReactionTypes(channel);
      // keep identity stable when nothing changed
      if (next.length === previous.length && next.every((t, i) => t === previous[i])) {
        return previous;
      }
      return next;
    });
  }, [channel]);

  useEffect(() => {
    setExtraTypes(collectExtraReactionTypes(channel));
    if (!channel) return undefined;
    const events = ["reaction.new", "reaction.updated", "reaction.deleted", "message.new", "message.updated"];
    const subscriptions = events.map((eventName) => channel.on(eventName, refresh));
    return () => subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
  }, [channel, refresh]);

  const reactionOptions = useMemo(() => buildReactionOptions(extraTypes), [extraTypes]);
  return { reactionOptions, refreshReactionOptions: refresh };
};

// Older pages and thread replies land in channel.state without a websocket
// event, so re-scan whenever the loaded message/thread set changes. Rendered
// inside <Channel> (the hook above lives outside it).
export const ReactionOptionsHistorySync = ({ onSync }) => {
  const { messages, threadMessages } = useChannelStateContext("ReactionOptionsHistorySync");
  useEffect(() => {
    onSync?.();
  }, [onSync, messages, threadMessages]);
  return null;
};
